import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { raiseToast } from '@fiverr-private/toast';
import { Job } from '../../lib/comeet';
import { appTranslate } from '../i18n';
import { useBI } from '../bi';
import { CvParseError, extractCvText } from './cvParser';
import { useTurnstile } from './turnstile';
import { matchResume, ResumeMatchHttpError } from './resumeMatch';

const cvFailureReason = (e: unknown): string => {
    if (e instanceof CvParseError) {
        return e.copyKey.replace('cv_match.', '');
    }
    if (e instanceof ResumeMatchHttpError) {
        return e.status ? `http_${e.status}` : 'network';
    }
    return 'unknown';
};

export type CvMatchStatus = 'idle' | 'parsing' | 'matching' | 'done' | 'error';

interface CvMatchState {
    status: CvMatchStatus;
    fileName: string | null;
    error: string | null;
    /** Positions that matched, mapped to full Job records, in rank order. */
    matchedJobs: Job[];
    /** A result has come back (match or no-match). */
    hasResult: boolean;
    /** Result came back with at least one fitting role. */
    isMatch: boolean;
    submit: (file: File) => Promise<void>;
    reset: () => void;
}

export const CvMatchContext = createContext<CvMatchState | null>(null);

interface CvMatchProviderProps {
    comeetPositions: Job[];
    children: React.ReactNode;
}

export const CvMatchProvider = ({ comeetPositions, children }: CvMatchProviderProps) => {
    const [status, setStatus] = useState<CvMatchStatus>('idle');
    const [fileName, setFileName] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [matchedJobs, setMatchedJobs] = useState<Job[]>([]);
    const { getToken } = useTurnstile();
    const bi = useBI();

    const submit = useCallback(
        async (file: File) => {
            setError(null);
            setMatchedJobs([]);
            setFileName(file.name);
            let stage: 'parsing' | 'matching' = 'parsing';
            try {
                setStatus('parsing');
                const cvText = await extractCvText(file);
                stage = 'matching';
                setStatus('matching');
                const turnstileToken = await getToken();
                const result = await matchResume(cvText, turnstileToken);
                // Uploaded document isn't a resume (org chart, article, …) — distinct error, not "no match".
                if (result.isResume === false) {
                    bi.reportCvUploadFailed({ stage: 'not_a_cv' });
                    setError(appTranslate('cv_match.not_a_cv') as string);
                    setStatus('error');
                    return;
                }
                // Backend returns only positions scoring > 50, in rank order. Map ids → full Job records.
                const byId = new Map(comeetPositions.map((job) => [job.uid, job]));
                const jobs = result.rankedPositions
                    .map((position) => byId.get(position.id))
                    .filter((job): job is Job => Boolean(job));
                if (jobs.length) {
                    bi.reportCvUploadSuccess({ matchedPositionUids: jobs.map((job) => job.uid) });
                } else {
                    bi.reportCvUploadNoMatch();
                }
                setMatchedJobs(jobs);
                setStatus('done');
            } catch (e) {
                bi.reportCvUploadFailed({ stage, reason: cvFailureReason(e) });
                // All failures surface as a toast and return the control to idle for a retry.
                let copyKey: Parameters<typeof appTranslate>[0] = 'cv_match.error_generic';
                if (e instanceof ResumeMatchHttpError && e.status === 429) {
                    copyKey = 'cv_match.error_rate_limited';
                } else if (e instanceof CvParseError) {
                    // CvParseError.copyKey values are cv_match.* keys defined in copies.json.
                    copyKey = e.copyKey as typeof copyKey;
                }
                raiseToast({ message: appTranslate(copyKey), type: 'error', location: 'top' });
                setStatus('idle');
                setFileName(null);
            }
        },
        [comeetPositions, getToken, bi]
    );

    const reset = useCallback(() => {
        setStatus('idle');
        setFileName(null);
        setError(null);
        setMatchedJobs([]);
    }, []);

    const value = useMemo<CvMatchState>(
        () => ({
            status,
            fileName,
            error,
            matchedJobs,
            hasResult: status === 'done',
            isMatch: status === 'done' && matchedJobs.length > 0,
            submit,
            reset,
        }),
        [status, fileName, error, matchedJobs, submit, reset]
    );

    return <CvMatchContext.Provider value={value}>{children}</CvMatchContext.Provider>;
};

export const useCvMatch = (): CvMatchState => {
    const ctx = useContext(CvMatchContext);
    if (!ctx) {
        throw new Error('useCvMatch must be used within a CvMatchProvider');
    }
    return ctx;
};
